import {
  useEffect,
  useState,
} from 'react'
import {
  useDispatch,
  useSelector,
} from 'react-redux'

import {
  Box,
  Button,
  Center,
  Input,
  Link,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react'

import FormHeading from './ui/FormHeading'

import {
  createJourney,
  editJourney,
} from '../features/journeys/journeySlice'

import { useFormik } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'

import StationSearch from './StationSearch'
import ViaPointSubform from './ViaPointSubform'
import TripSubform from './trips/TripSubform'
import LengthSubform from './LengthSubform'
import RoutePreviewButton from './RoutePreviewButton'
import FormElement from './ui/FormElement'
import DatesSubform from './DatesSubform'
import RoutingOptionsSubform from './RoutingOptionsSubform'

import { canCreateJourney } from '../utilities/journeyCreateHelper'
import UserReportPopover from './UserReportPopover'
import { MdFeedback } from 'react-icons/md'

export default function JourneyForm({ editPrefilledJourney, editModalOnClose, setPreviewPoints }) {

  const { user } = useSelector((state) => state.auth)

  const dispatch = useDispatch()

  const [destinationStation, setDestinationStation] = useState(undefined) // Used for storing the previous destination that becomes the origin after submit
  const [permittedToCreateJourney, setPermittedToCreateJourney] = useState(true)

  const formik = useFormik({
    initialValues: {
      origin: undefined,
      destination: undefined,
      viaPointsIds: [],
      date: moment(),
      partOfTrip: undefined,
      length: 0,
      operator: '',
      note: '',
      routingOptions: undefined,
    },
    validationSchema: Yup.object({
      origin: Yup.object().shape({
        _id: Yup.string().required("Origin station is required"),
      }),
      destination: Yup.object().shape({
        _id: Yup.string().required("Destination station is required"),
      }),
    }),
    onSubmit: (values) => {
      if (editPrefilledJourney === undefined) { // New journey
        dispatch(createJourney({
          originId: values.origin._id,
          destinationId: values.destination._id,
          viaPointsIds: values.viaPointsIds,
          length: values.length,
          date: values.date.toDate(),
          partOfTrip: values.partOfTrip,
          operator: values.operator,
          note: values.note,
          routingOptions: values.routingOptions,
        }))

        // Setting previous destination to become origin after submit
        formik.setFieldValue('destination', undefined)
        formik.setFieldValue('origin', destinationStation)
      } else { // Editing journey
        dispatch(editJourney({
          journeyId: editPrefilledJourney._id,
          originId: values.origin._id,
          destinationId: values.destination._id,
          length: values.length,
          date: values.date.toDate(),
          partOfTrip: values.partOfTrip,
          operator: values.operator,
          note: values.note,
          routingOptions: values.routingOptions,
        }))

        editModalOnClose()
        formik.resetForm()
      }
    }
  })

  useEffect(() => {
    if (editPrefilledJourney !== undefined) {
      formik.setFieldValue('origin', editPrefilledJourney.from)
      formik.setFieldValue('destination', editPrefilledJourney.to)
      formik.setFieldValue('date', moment(editPrefilledJourney.date))
      formik.setFieldValue('partOfTrip', Date.parse(editPrefilledJourney.partOfTrip))
      formik.setFieldValue('lengthHrs', Math.floor(editPrefilledJourney.length / 60))
      formik.setFieldValue('lengthMins', (editPrefilledJourney.length % 60))
      formik.setFieldValue('operator', editPrefilledJourney.operator)
      formik.setFieldValue('note', editPrefilledJourney.note)
    }
  }, [editPrefilledJourney])

  useEffect(() => {
    if (formik.values.origin === undefined || formik.values.destination === undefined)
      return
    
    setPermittedToCreateJourney(canCreateJourney(user, {origin: formik.values.origin.country.code, destination: formik.values.destination.country.code}))
  }, [formik.values.origin, formik.values.destination])

  useEffect(() => {
    if (editPrefilledJourney !== undefined)
      return
    
    setPreviewPoints({
      origin: formik.values.origin,
      destination: formik.values.destination,
    })
  }, [formik.values.origin, formik.values.destination])

  return (
    <>
      <Box className='journey-form'>
        <form onSubmit={formik.handleSubmit}>

          <FormElement>
            <FormHeading>
              From:
            </FormHeading>
            <StationSearch
              setSelectedStation={(station) => {
                station && (formik.setFieldValue('origin', station))
              }}
              prefilledText={(editPrefilledJourney ? editPrefilledJourney.from.name : (destinationStation !== undefined ? destinationStation.name : ''))}
            />
          </FormElement>

          <FormElement>
            {editPrefilledJourney === undefined ? (
              <ViaPointSubform
                setViaPointsIds={(idsArray) => formik.values.viaPointsIds = idsArray}
              />
            ) : (
              <Text
                fontSize='sm'
              >
                Editing via points is currently not possible. Please delete & re-add instead.
              </Text>)
            }
          </FormElement>

          <FormElement>
            <FormHeading>
              To:
            </FormHeading>
            <StationSearch
              setSelectedStation={(station) => {
                if (station !== undefined) {
                  station && (formik.setFieldValue('destination', station))
                  setDestinationStation(station)
                }
              }}
              prefilledText={(editPrefilledJourney ? editPrefilledJourney.to.name : '')}
            />
          </FormElement>

          { !permittedToCreateJourney && 
          <FormElement>
            <Text fontSize='sm' color='red.500'>
              {user.details.subscription.tier === 'free-standard' && 'An All-Country Pass is required to add journeys fully outside your home country.'}
              {user.details.subscription.tier === 'free-restricted' && 'An All-Country Pass is required to add this journey'}
              
              <Link href='/pricing' color='blue.500' ml='1'>Learn more. </Link>
            </Text> 
          </FormElement>}
            
          {editPrefilledJourney === undefined &&
            <RoutePreviewButton
              formValues={formik.values}
              isDisabled={!permittedToCreateJourney}
            />
          }

          <FormElement>
            <FormHeading>
              Date:
            </FormHeading>
            <DatesSubform 
              setDate={(date) => formik.setFieldValue('date', (date))}
              // prefilledDate={editPrefilledJourney ? editPrefilledJourney.date : undefined}
              prefilledDate={editPrefilledJourney ? moment(editPrefilledJourney.date) : formik.values.date}
            />
          </FormElement>

          <FormElement>
            <FormHeading>
              Trip:
            </FormHeading>
            <TripSubform
              preselectedTripId={editPrefilledJourney ? editPrefilledJourney.partOfTrip : undefined}
              setSelectedTrip={(id) => formik.setFieldValue('partOfTrip', (id))}
              isEditing={editPrefilledJourney !== undefined}
            />
          </FormElement>

          <FormElement>
            <FormHeading>
              Duration:
            </FormHeading>
            <LengthSubform
              setLength={(length) => formik.setFieldValue('length', (length))}
              prefilledLength={editPrefilledJourney ? editPrefilledJourney.length : undefined}
            />
          </FormElement>

          {user.preferences.isOperatorEnabled ? (
            <FormElement>
              <FormHeading>
                Operator
              </FormHeading>
              <Input
                placeholder='Operator'
                id="operator"
                name="operator"
                value={formik.values.operator}
                onChange={formik.handleChange}
              />
            </FormElement>
          ) : (<></>)}

          <FormElement>
              <Input
                placeholder='Optional note'
                id="note"
                name="note"
                value={formik.values.note}
                onChange={formik.handleChange}
              />
          </FormElement>

          <FormElement>
            <RoutingOptionsSubform 
              setRoutingOptions={(routingOptions) => formik.setFieldValue('routingOptions', (routingOptions))}
            />
          </FormElement>

          <FormElement>
            <Stack direction='row' align="center" justify="center" width="full">
              <Spacer />
              <Button
                type="submit"
                isDisabled={!permittedToCreateJourney}
              >
                {!editPrefilledJourney ? 'Create journey' : 'Edit journey'}
              </Button>
              <Spacer />
              { !editPrefilledJourney && (
                <UserReportPopover type='message'>
                  <Button variant='outline' size='sm'><MdFeedback /></Button>
                </UserReportPopover>
              )}
            </Stack>
          </FormElement>
        </form>
      </Box>
    </>
  )
}
